import React, { useEffect, useCallback } from "react";
import { EditorObject } from "../types/editorTypes";

interface TextEditOverlayProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  editingTextPosition: { x: number; y: number; width: number; height: number };
  editingTextValue: string;
  setEditingTextValue: (value: string) => void;
  handleTextEditConfirm: (newHeight?: number) => void;
  editingObj: EditorObject | undefined;
  // デフォルト値用
  textFontSize: number;
  textFontFamily: string;
  textFontStyle: 'normal' | 'bold' | 'italic';
  textColor: string;
}

export const TextEditOverlay: React.FC<TextEditOverlayProps> = ({
  textareaRef,
  editingTextPosition,
  editingTextValue,
  setEditingTextValue,
  handleTextEditConfirm,
  editingObj,
  textFontSize,
  textFontFamily,
  textFontStyle,
  textColor,
}) => {
  // テキストエリアの高さを自動調整
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 一度高さをリセットしてからscrollHeightを取得
      textarea.style.height = 'auto';
      const newHeight = Math.max(editingTextPosition.height, textarea.scrollHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [textareaRef, editingTextPosition.height]);

  // 値が変わったら高さを調整
  useEffect(() => {
    adjustHeight();
  }, [editingTextValue, adjustHeight]);

  // マウント時にも調整
  useEffect(() => {
    // 少し遅延させてフォントが読み込まれた後に調整
    const timer = setTimeout(adjustHeight, 10);
    return () => clearTimeout(timer);
  }, [adjustHeight]);

  return (
    <textarea
      ref={textareaRef}
      value={editingTextValue}
      onChange={(e) => setEditingTextValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          const textarea = textareaRef.current;
          const newHeight = textarea ? textarea.scrollHeight : undefined;
          handleTextEditConfirm(newHeight);
        }
      }}
      onBlur={() => {
        const textarea = textareaRef.current;
        const newHeight = textarea ? textarea.scrollHeight : undefined;
        handleTextEditConfirm(newHeight);
      }}
      className="outline-none resize-none"
      style={{
        position: 'absolute',
        left: editingTextPosition.x,
        top: editingTextPosition.y,
        width: editingTextPosition.width,
        minHeight: editingTextPosition.height,
        zIndex: 100,
        fontSize: `${editingObj?.fontSize || textFontSize}px`,
        fontFamily: editingObj?.fontFamily || textFontFamily,
        fontWeight: (editingObj?.fontStyle || textFontStyle) === 'bold' ? 'bold' : 'normal',
        color: editingObj?.textColor || textColor,
        backgroundColor: (editingObj?.backgroundColor && editingObj.backgroundColor !== 'transparent')
          ? editingObj.backgroundColor
          : 'transparent',
        border: 'none',
        padding: '0px',
        lineHeight: '1.2',
        overflow: 'hidden',
        textAlign: editingObj?.textAlign || 'left',
      }}
      autoFocus
    />
  );
};

export default TextEditOverlay;
